import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { credentialsInterceptor } from '@core/interceptors/credentials.interceptor';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { languageInterceptor } from '@core/interceptors/language.interceptor';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { primeNgTheme } from '@theme/primeng-theme.config';
import { provideHashbrown } from '@hashbrownai/angular';

import { routes } from './app.routes';
import { Configuration } from '@generated/configuration';
import { environment } from '@env/environment';
import { getAssistantAccessToken, setAssistantAuthService } from '@core/services/assistant-auth-bridge';
import { PrimeNgThemeGuard } from '@core/services/primeng-theme-guard';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptors([credentialsInterceptor, languageInterceptor, errorInterceptor])),
    {
      provide: Configuration,
      useFactory: () => ({ rootUrl: environment.generatedApiUrl })
    },
    // Тема — общая с widget-бандлом (@theme/primeng-theme.config), потому
    // что виджет поднимается в этом же документе и переписывает те же
    // style-элементы PrimeNG. Подробности — в самом файле.
    providePrimeNG({ theme: primeNgTheme }),
    MessageService,
    provideHashbrown({
      baseUrl: `${environment.hashbrownProxyUrl}/api/chat`,
      middleware: [
        // Hashbrown.Chat.Middleware = (RequestInit) => RequestInit | Promise<...>
        // Runs per-request → token всегда свежий (Keycloak сам рефрешит).
        (init) => {
          const token = getAssistantAccessToken();
          if (!token) return init;
          return {
            ...init,
            headers: {
              ...(init.headers as Record<string, string> | undefined),
              Authorization: `Bearer ${token}`,
            },
          };
        },
      ],
    }),
    provideAppInitializer(async () => {
      // Сторож темы поднимаем ДО авторизации: страница агента подключает
      // widget-бандл со своим PrimeNG, и тот перезаписывает наши
      // style-элементы. Подробности — в самом сервисе.
      inject(PrimeNgThemeGuard).start();

      const auth = inject(AuthService);
      setAssistantAuthService(auth);
      await auth.init();
    }),
  ]
};
